/**
 * The reel Seth plans in the Creative Studio (Gary, 2 Nov 2026). Seth ends a Studio answer with one fenced block:
 *
 *   ```reel
 *   { "shots": [{ "clip": "<Content Library video id>", "from": 0, "to": 3, "text": "What is that blue light?" }, ...],
 *     "close": "Book your bed clean", "post": { "title": "...", "text": "...", "hashtags": "...", "theme": "behind_scenes" } }
 *   ```
 *
 * Seth picks clips from their descriptions and cannot watch them, so his times are a starting point: the person
 * watches each shot in the Studio and sets the start and end. Pure, so it can be tested.
 */

import type { PostDraft } from './cardSpec';

export interface ReelShot {
  clip: string;
  from: number;
  to: number;
  text: string;
}

export interface ReelSpec {
  shots: ReelShot[];
  /** Words on the closing brand frame; no closing frame when empty. */
  close: string;
  post: PostDraft;
}

export const MAX_SHOTS = 8;
/** Shortest and longest shot, and the longest reel, in seconds. */
export const SHOT_MIN = 1;
export const SHOT_MAX = 20;
export const REEL_MAX = 60;
/** How long the closing brand frame shows. */
export const CLOSE_SECONDS = 3;

const THEMES = ['learn', 'review', 'partner', 'doctor', 'customer', 'behind_scenes', 'nudge'];
const FENCE = /```reel[^\n]*\n([\s\S]*?)(```|$)/g;

/** The last complete reel block in an answer, or null when there is none. */
export function lastReelBlock(text: string): string | null {
  let found: string | null = null;
  for (const m of text.matchAll(FENCE)) {
    if (m[2] === '```') found = (m[1] ?? '').trim();
  }
  return found;
}

/** The answer as the chat shows it: each reel block, finished or still arriving, becomes one plain line. */
export function stripReelBlocks(text: string): string {
  return text.replace(FENCE, 'Reel plan updated in the preview.');
}

function str(v: unknown, max: number): string {
  return typeof v === 'string' ? v.trim().slice(0, max) : '';
}

function secs(v: unknown): number | null {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 10) / 10 : null;
}

/** A shot's start and end kept sensible: end after start, between SHOT_MIN and SHOT_MAX long. */
export function clampShot(from: number, to: number): { from: number; to: number } {
  const start = Math.max(0, Math.round(from * 10) / 10);
  let end = Math.round(to * 10) / 10;
  if (!(end > start)) end = start + 3;
  end = Math.min(Math.max(end, start + SHOT_MIN), start + SHOT_MAX);
  return { from: start, to: Math.round(end * 10) / 10 };
}

/** The reel's length in seconds: every shot plus the closing frame. */
export function reelLength(shots: Pick<ReelShot, 'from' | 'to'>[], close: string): number {
  const total = shots.reduce((s, x) => s + (x.to - x.from), 0) + (close ? CLOSE_SECONDS : 0);
  return Math.round(total * 10) / 10;
}

/** Reads Seth's reel block. A broken block says what is wrong. */
export function parseReelSpec(block: string): ReelSpec | { error: string } {
  let raw: unknown;
  try {
    raw = JSON.parse(block);
  } catch {
    return { error: 'Seth sent a reel plan the Studio could not read. Ask him to send it again.' };
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { error: 'Seth sent an empty reel plan.' };
  const o = raw as Record<string, unknown>;
  const list = Array.isArray(o.shots) ? o.shots.slice(0, MAX_SHOTS) : [];
  const shots: ReelShot[] = [];
  for (const s of list) {
    const shot = (s && typeof s === 'object' ? s : {}) as Record<string, unknown>;
    const clip = str(shot.clip, 64);
    if (!clip) continue;
    const from = secs(shot.from) ?? 0;
    shots.push({ clip, ...clampShot(from, secs(shot.to) ?? from + 3), text: str(shot.text, 60) });
  }
  if (shots.length === 0) return { error: 'The reel plan has no shots. Ask Seth to pick the clips.' };

  const p = (o.post && typeof o.post === 'object' ? o.post : {}) as Record<string, unknown>;
  const theme = str(p.theme, 20);
  return {
    shots,
    close: str(o.close, 60),
    post: {
      title: str(p.title, 120) || shots[0]?.text || 'Studio reel',
      text: str(p.text, 2200),
      hashtags: str(p.hashtags, 300),
      theme: THEMES.includes(theme) ? theme : 'behind_scenes',
    },
  };
}
